import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Form,
  Input,
  Select,
  DatePicker,
  Modal,
  message,
  Statistic,
  Row,
  Col
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined
} from '@ant-design/icons';
import api from '../../utils/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const auditStatusMap = {
  approved: { color: 'green', text: '已通过' },
  pending: { color: 'orange', text: '未匹配' },
  skipped: { color: 'default', text: '已跳过' }
};

const orderTypeMap = {
  trade: { color: 'blue', text: '收款单' },
  withdrawal: { color: 'purple', text: '代付单' }
};

const EmailRecordList = () => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({});
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [searchForm] = Form.useForm();

  const loadData = async (params = {}) => {
    setLoading(true);
    try {
      const response = await api.get('/admin/email-records', {
        params: {
          page: pagination.current,
          limit: pagination.pageSize,
          ...filters,
          ...params
        }
      });

      if (response.code === 200) {
        setDataSource(response.data.list);
        setPagination({
          ...pagination,
          total: response.data.pagination.total,
          current: response.data.pagination.page
        });
      }
    } catch (error) {
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await api.get('/admin/email-records/statistics', {
        params: filters
      });

      if (response.code === 200) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('加载统计数据失败', error);
    }
  };

  useEffect(() => {
    loadData();
    loadStatistics();
  }, []);

  const handleSearch = (values) => {
    const newFilters = {
      ...values,
      startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: values.dateRange?.[1]?.format('YYYY-MM-DD')
    };
    delete newFilters.dateRange;

    setFilters(newFilters);
    setPagination({ ...pagination, current: 1 });
    loadData({ ...newFilters, page: 1 });
    loadStatistics();
  };

  const handleReset = () => {
    searchForm.resetFields();
    setFilters({});
    setPagination({ ...pagination, current: 1 });
    loadData({ page: 1 });
    loadStatistics();
  };

  const handleViewDetail = (record) => {
    setCurrentRecord(record);
    setDetailModalVisible(true);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80
    },
    {
      title: '发件人',
      dataIndex: 'sender',
      width: 180,
      ellipsis: true,
      render: (sender) => sender || '-'
    },
    {
      title: '邮件主题',
      dataIndex: 'subject',
      width: 240,
      ellipsis: true,
      render: (subject) => subject || '-'
    },
    {
      title: '验证短码',
      dataIndex: 'short_code',
      width: 120,
      render: (code) => code || '-'
    },
    {
      title: '金额',
      dataIndex: 'amount',
      width: 120,
      render: (amount, record) => amount ? `${amount.toLocaleString()} ${record.currency_code || ''}`.trim() : '-'
    },
    {
      title: '匹配订单',
      dataIndex: 'matched_trade_no',
      width: 160,
      ellipsis: true,
      render: (no) => no || '-'
    },
    {
      title: '类型',
      dataIndex: 'order_type',
      width: 90,
      render: (type) => {
        const config = orderTypeMap[type];
        return config ? <Tag color={config.color}>{config.text}</Tag> : <span style={{ color: '#999' }}>-</span>;
      }
    },
    {
      title: '状态',
      dataIndex: 'audit_status',
      width: 100,
      render: (status) => {
        const config = auditStatusMap[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 160,
      render: (time) => dayjs(time).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
        >
          详情
        </Button>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      {statistics && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="总记录数"
                value={statistics.totalCount}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          {statistics.byStatus.map((stat) => (
            <Col span={6} key={stat.status}>
              <Card>
                <Statistic
                  title={stat.statusName}
                  value={stat.count}
                  valueStyle={{
                    color:
                      stat.status === 'approved'
                        ? '#52c41a'
                        : stat.status === 'pending'
                        ? '#faad14'
                        : '#8c8c8c'
                  }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Card style={{ marginBottom: 16 }}>
        <Form form={searchForm} layout="inline" onFinish={handleSearch}>
          <Form.Item name="shortCode" label="验证短码">
            <Input placeholder="请输入验证短码" style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="sender" label="发件人">
            <Input placeholder="请输入发件人" style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="matchedTradeNo" label="订单号">
            <Input placeholder="请输入订单号" style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="auditStatus" label="状态">
            <Select placeholder="请选择" style={{ width: 120 }} allowClear>
              <Option value="approved">已通过</Option>
              <Option value="pending">未匹配</Option>
              <Option value="skipped">已跳过</Option>
            </Select>
          </Form.Item>
          <Form.Item name="orderType" label="类型">
            <Select placeholder="请选择" style={{ width: 100 }} allowClear>
              <Option value="trade">收款单</Option>
              <Option value="withdrawal">代付单</Option>
            </Select>
          </Form.Item>
          <Form.Item name="dateRange" label="日期范围">
            <RangePicker />
          </Form.Item>
          <Form.Item name="keyword" label="关键词">
            <Input placeholder="搜索主题/内容/短码" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleReset} icon={<ReloadOutlined />}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Button icon={<ReloadOutlined />} onClick={() => loadData()}>
            刷新
          </Button>
        </Space>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`
          }}
          onChange={(newPagination) => {
            setPagination(newPagination);
            loadData({ page: newPagination.current, limit: newPagination.pageSize });
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      <Modal
        title="邮件记录详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={700}
      >
        {currentRecord && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <p><strong>ID：</strong>{currentRecord.id}</p>
              </Col>
              <Col span={12}>
                <p><strong>消息ID：</strong>{currentRecord.message_id || '-'}</p>
              </Col>
              <Col span={12}>
                <p><strong>发件人：</strong>{currentRecord.sender || '-'}</p>
              </Col>
              <Col span={12}>
                <p><strong>邮件主题：</strong>{currentRecord.subject || '-'}</p>
              </Col>
              <Col span={12}>
                <p><strong>验证短码：</strong>
                  <span style={{ fontWeight: 'bold', color: '#389e0d' }}>{currentRecord.short_code || '-'}</span>
                </p>
              </Col>
              <Col span={12}>
                <p><strong>金额：</strong>
                  {currentRecord.amount
                    ? `${currentRecord.amount.toLocaleString()} ${currentRecord.currency_code || ''}`.trim()
                    : '-'}
                </p>
              </Col>
              <Col span={12}>
                <p><strong>匹配订单：</strong>{currentRecord.matched_trade_no || '-'}</p>
              </Col>
              <Col span={12}>
                <p><strong>订单类型：</strong>
                  {currentRecord.order_type ? (
                    <Tag color={orderTypeMap[currentRecord.order_type]?.color || 'default'}>
                      {orderTypeMap[currentRecord.order_type]?.text || currentRecord.order_type}
                    </Tag>
                  ) : '-'}
                </p>
              </Col>
              <Col span={12}>
                <p><strong>接收时间：</strong>
                  {currentRecord.received_at
                    ? dayjs(currentRecord.received_at).format('YYYY-MM-DD HH:mm:ss')
                    : '-'}
                </p>
              </Col>
              <Col span={24}>
                <p><strong>邮件内容：</strong></p>
                <div style={{ padding: 12, background: '#f5f5f5', borderRadius: 4, marginBottom: 12, whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto' }}>
                  {currentRecord.content || '-'}
                </div>
              </Col>
              <Col span={24}>
                <p>
                  <strong>状态：</strong>
                  <Tag
                    color={auditStatusMap[currentRecord.audit_status]?.color || 'default'}
                    style={{ marginLeft: 8 }}
                  >
                    {auditStatusMap[currentRecord.audit_status]?.text || currentRecord.audit_status}
                  </Tag>
                </p>
              </Col>
              {currentRecord.audit_remark && (
                <Col span={24}>
                  <p><strong>备注：</strong></p>
                  <div style={{ padding: 12, background: '#f5f5f5', borderRadius: 4, marginBottom: 12 }}>
                    {currentRecord.audit_remark}
                  </div>
                </Col>
              )}
              <Col span={12}>
                <p><strong>创建时间：</strong>{dayjs(currentRecord.created_at).format('YYYY-MM-DD HH:mm:ss')}</p>
              </Col>
              <Col span={12}>
                <p><strong>更新时间：</strong>{dayjs(currentRecord.updated_at).format('YYYY-MM-DD HH:mm:ss')}</p>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EmailRecordList;
